import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { ArrowLeft, Check } from '@phosphor-icons/react';
import { ProductIcon, IconWithTransom } from '../components/WindowIcons';
import { productCategories } from '../config/productConfig';
import { selectionFlows, multiStepCategories, mapOpeningToConfigId } from '../config/windowSelectionFlows';

export default function ProductSelectorModal({ 
  isOpen, 
  onClose, 
  onSelectProduct, 
  priceList 
}) {
  // Navigation state
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  // Multi-step flow state
  const [flowStep, setFlowStep] = useState(null); // 'type' | 'transom' | 'opening' | 'panel' | 'atypical'
  const [selectedTypeOption, setSelectedTypeOption] = useState(null);
  const [selectedTransom, setSelectedTransom] = useState(null);
  const [selectedOpening, setSelectedOpening] = useState(null);

  const resetState = () => {
    setSelectedCategory(null);
    setFlowStep(null);
    setSelectedTypeOption(null);
    setSelectedTransom(null);
    setSelectedOpening(null);
  };

  const panelOptions = selectedCategory ? selectionFlows[selectedCategory.id]?.panelOptions : null;

  const handleClose = () => {
    resetState();
    onClose();
  };

  // When user selects a category from the grid
  const handleCategorySelect = (category) => {
    const flow = selectionFlows[category.id];
    
    if (flow && multiStepCategories.includes(category.id) && !flow.useSimpleFlow) {
      // Multi-step flow
      setSelectedCategory(category);
      setFlowStep('type');
    } else {
      // Simple flow (old behavior for non-multi-step categories)
      setSelectedCategory(category);
      setFlowStep('simple');
    }
  };

  // Step 1: Select window type (1 anta, 2 ante, etc.)
  const handleTypeSelect = (option) => {
    setSelectedTypeOption(option);
    
    if (option.isAtypical) {
      setFlowStep('atypical');
    } else if (option.transomOptions && option.transomOptions.length > 1) {
      // Has transom choices -> go to step 2
      setFlowStep('transom');
    } else if (option.transomOptions && option.transomOptions.length === 1) {
      // Single transom option -> auto-select it
      const defaultTransom = option.transomOptions[0];
      setSelectedTransom(defaultTransom);
      const openingTypes = defaultTransom.openingTypes || option.openingTypes;
      if (openingTypes && openingTypes.length > 0) {
        setFlowStep('opening');
      } else {
        const subcatId = defaultTransom.subcategoryId || option.subcategoryId;
        if (subcatId) finalizeSelection(subcatId, null);
      }
    } else if (option.openingTypes && option.openingTypes.length > 0) {
      setFlowStep('opening');
    } else {
      // Direct selection (e.g. scorrevoli with subcategoryId)
      const subcatId = option.subcategoryId;
      if (subcatId) finalizeSelection(subcatId, null);
    }
  };

  // Step 2: Select transom option
  const handleTransomSelect = (transom) => {
    setSelectedTransom(transom);
    
    // Check openingTypes on transom first, then on typeOption
    const openingTypes = transom.openingTypes || selectedTypeOption.openingTypes;
    if (openingTypes && openingTypes.length > 0) {
      setFlowStep('opening');
    } else {
      // No opening types -> finalize directly
      finalizeSelection(transom.subcategoryId || selectedTypeOption.subcategoryId, null);
    }
  };

  const currentSubcategoryId = () => selectedTransom?.subcategoryId
    || selectedTypeOption?.subcategoryId
    || selectedTypeOption?.transomOptions?.[0]?.subcategoryId;

  // Step 3: Select opening type
  const handleOpeningSelect = (opening) => {
    if (panelOptions?.length) {
      // Porte d'ingresso: choose the panel before adding the product
      setSelectedOpening(opening);
      setFlowStep('panel');
      return;
    }
    finalizeSelection(currentSubcategoryId(), opening);
  };

  // Step 4 (porte): Select door panel
  const handlePanelSelect = (panel) => {
    finalizeSelection(currentSubcategoryId(), selectedOpening, panel);
  };

  // Handle atypical shape selection
  const handleAtypicalSelect = (atypical) => {
    finalizeSelection(atypical.subcategoryId, null);
  };

  // Simple flow subcategory selection (old behavior)
  const handleSimpleSubcategorySelect = (subcategoryId) => {
    finalizeSelection(subcategoryId, null);
  };

  // Finalize: tell parent which product was selected
  const finalizeSelection = (subcategoryId, opening, panel = null) => {
    if (!selectedCategory || !subcategoryId) return;
    
    const openingTypeId = opening ? mapOpeningToConfigId(opening.id) : null;
    const openingDetail = opening?.name || null;
    const openingIconKey = opening?.icon || null;
    const transomType = selectedTransom?.id || null; // 'con_sopraluce', 'con_sottoluce', or null
    
    onSelectProduct({
      categoryId: selectedCategory.id,
      subcategoryId,
      openingTypeId,
      openingDetail,
      openingIconKey,
      transomType,
      doorPanel: panel?.id || null,
    });
    
    resetState();
  };

  // Go back one step
  const handleBack = () => {
    if (flowStep === 'panel') {
      setFlowStep('opening');
      setSelectedOpening(null);
    } else if (flowStep === 'opening') {
      if (selectedTypeOption?.transomOptions?.length > 1) {
        setFlowStep('transom');
        setSelectedTransom(null);
      } else {
        setFlowStep('type');
        setSelectedTypeOption(null);
        setSelectedTransom(null);
      }
    } else if (flowStep === 'transom') {
      setFlowStep('type');
      setSelectedTypeOption(null);
    } else if (flowStep === 'atypical') {
      setFlowStep('type');
      setSelectedTypeOption(null);
    } else {
      resetState();
    }
  };

  // Get current dialog title
  const getTitle = () => {
    if (!selectedCategory) return 'Seleziona Tipo Prodotto';
    const flow = selectionFlows[selectedCategory.id];
    if (flowStep === 'type') return flow?.steps?.[0]?.title || selectedCategory.name;
    if (flowStep === 'transom') return 'SOPRALUCE / SOTTOLUCE';
    if (flowStep === 'opening') return 'TIPO DI APERTURA';
    if (flowStep === 'panel') return 'PANNELLO';
    if (flowStep === 'atypical') return 'FORME ATIPICHE';
    return selectedCategory.name;
  };

  // Get subtitle
  const getSubtitle = () => {
    const flow = selectionFlows[selectedCategory?.id];
    if (flowStep === 'type') return flow?.steps?.[0]?.subtitle || '';
    if (flowStep === 'transom') return 'Seleziona se aggiungere sopraluce o sottoluce';
    if (flowStep === 'opening') return 'Seleziona il tipo di apertura delle ante';
    if (flowStep === 'panel') return 'Seleziona il tipo di pannello della porta';
    if (flowStep === 'atypical') return 'Seleziona la forma speciale';
    return '';
  };

  // Get step indicator
  const getStepIndicator = () => {
    if (!selectedCategory || flowStep === 'simple') return null;
    const flow = selectionFlows[selectedCategory.id];
    if (!flow || flow.useSimpleFlow) return null;
    
    const steps = [
      { key: 'type', label: 'Tipo' },
      { key: 'transom', label: 'Sopraluce' },
      { key: 'opening', label: 'Apertura' },
    ].concat(flow.panelOptions ? [{ key: 'panel', label: 'Pannello' }] : []);
    
    const currentIdx = steps.findIndex(s => s.key === flowStep);
    
    return (
      <div className="flex items-center gap-2 mb-4" data-testid="step-indicator">
        {steps.map((step, idx) => (
          <div key={step.key} className="flex items-center gap-2">
            <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
              idx < currentIdx ? 'bg-[#0033A0] text-white' :
              idx === currentIdx ? 'bg-[#0033A0]/10 text-[#0033A0] ring-1 ring-[#0033A0]/30' :
              'bg-gray-100 text-gray-400'
            }`}>
              {idx < currentIdx ? (
                <Check size={12} weight="bold" />
              ) : (
                <span>{idx + 1}</span>
              )}
              <span>{step.label}</span>
            </div>
            {idx < steps.length - 1 && (
              <div className={`w-6 h-px ${idx < currentIdx ? 'bg-[#0033A0]' : 'bg-gray-200'}`} />
            )}
          </div>
        ))}
      </div>
    );
  };

  // Render category grid (initial screen)
  const renderCategoryGrid = () => (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 py-4">
      {productCategories.map((category) => (
        <button
          key={category.id}
          onClick={() => handleCategorySelect(category)}
          className="group p-3 border border-[#E2E8F0] rounded-sm hover:border-[#0033A0] hover:bg-[#F4F6F8] transition-all text-center"
          data-testid={`select-category-${category.id}`}
        >
          <div className="w-12 h-12 mx-auto mb-2 p-1.5 bg-[#F4F6F8] rounded-sm group-hover:bg-white">
            <ProductIcon type={category.icon} className="w-full h-full" />
          </div>
          <h3 className="text-xs font-medium text-[#0A0F1A] group-hover:text-[#0033A0]">
            {category.name}
          </h3>
        </button>
      ))}
    </div>
  );

  // Render type selection (Step 1)
  const renderTypeStep = () => {
    const flow = selectionFlows[selectedCategory.id];
    const options = flow.steps[0].options;
    
    return (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 py-2">
        {options.map((option) => (
          <button
            key={option.id}
            onClick={() => handleTypeSelect(option)}
            className={`group p-3 border rounded-sm transition-all text-center hover:border-[#0033A0] hover:bg-[#F4F6F8] ${
              selectedTypeOption?.id === option.id 
                ? 'border-[#0033A0] bg-[#0033A0]/5' 
                : 'border-[#E2E8F0]'
            }`}
            data-testid={`select-type-${option.id}`}
          >
            <div className="w-14 h-14 mx-auto mb-2 p-1 bg-[#F4F6F8] rounded-sm group-hover:bg-white">
              <ProductIcon type={option.icon} className="w-full h-full" />
            </div>
            <h3 className="text-xs font-medium text-[#0A0F1A] group-hover:text-[#0033A0]">
              {option.name}
            </h3>
          </button>
        ))}
      </div>
    );
  };

  // Render transom selection (Step 2)
  const renderTransomStep = () => {
    if (!selectedTypeOption?.transomOptions) return null;
    
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 py-2">
        {(selectedTypeOption.transomOptions || []).map((transom) => (
          <button
            key={transom.id}
            onClick={() => handleTransomSelect(transom)}
            className={`group p-3 border rounded-sm transition-all text-center hover:border-[#0033A0] hover:bg-[#F4F6F8] ${
              selectedTransom?.id === transom.id 
                ? 'border-[#0033A0] bg-[#0033A0]/5' 
                : 'border-[#E2E8F0]'
            }`}
            data-testid={`select-transom-${transom.id}`}
          >
            <div className="w-14 h-14 mx-auto mb-2 p-1 bg-[#F4F6F8] rounded-sm group-hover:bg-white">
              <ProductIcon type={transom.icon} className="w-full h-full" />
            </div>
            <h3 className="text-xs font-medium text-[#0A0F1A] group-hover:text-[#0033A0]">
              {transom.name}
            </h3>
          </button>
        ))}
      </div>
    );
  };

  // Render opening type selection (Step 3)
  const renderOpeningStep = () => {
    // Opening types can be on the transom or on the type option
    const openingTypes = selectedTransom?.openingTypes || selectedTypeOption?.openingTypes;
    if (!openingTypes) return null;
    
    return (
      <div>
        {/* Summary of previous selections */}
        <div className="flex items-center gap-3 mb-4 p-2 bg-[#F4F6F8] rounded-sm text-sm text-[#4A5568]">
          <div className="w-8 h-8 p-0.5">
            <ProductIcon type={selectedTypeOption.icon} className="w-full h-full" />
          </div>
          <span className="font-medium">{selectedTypeOption.name}</span>
          {selectedTransom && selectedTransom.id !== 'senza' && (
            <>
              <span className="text-[#0033A0]">+</span>
              <span>{selectedTransom.name}</span>
            </>
          )}
        </div>
        
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 py-2">
          {openingTypes.map((opening) => {
            const hasSopra = selectedTransom?.id === 'con_sopraluce';
            const hasSotto = selectedTransom?.id === 'con_sottoluce';
            return (
              <button
                key={opening.id}
                onClick={() => handleOpeningSelect(opening)}
                className="group p-2.5 border border-[#E2E8F0] rounded-sm hover:border-[#0033A0] hover:bg-[#F4F6F8] transition-all text-center"
                data-testid={`select-opening-${opening.id}`}
              >
                <div className="w-12 h-14 mx-auto mb-1.5 p-0.5 bg-[#F4F6F8] rounded-sm group-hover:bg-white">
                  {(hasSopra || hasSotto) ? (
                    <IconWithTransom type={opening.icon} position={hasSopra ? 'top' : 'bottom'} className="w-full h-full" />
                  ) : (
                    <ProductIcon type={opening.icon} className="w-full h-full" />
                  )}
                </div>
                <h3 className="text-[10px] leading-tight font-medium text-[#4A5568] group-hover:text-[#0033A0]">
                  {opening.name}
                </h3>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // Render door panel choice (Step 4, porte d'ingresso)
  const renderPanelStep = () => (
    <div>
      <div className="flex items-center gap-3 mb-4 p-2 bg-[#F4F6F8] rounded-sm text-sm text-[#4A5568]">
        <div className="w-8 h-8 p-0.5">
          <ProductIcon type={selectedOpening?.icon || selectedTypeOption?.icon} className="w-full h-full" />
        </div>
        <span className="font-medium">{selectedTypeOption?.name}</span>
        {selectedOpening && <><span className="text-[#0033A0]">·</span><span>{selectedOpening.name}</span></>}
      </div>
      <div className="grid grid-cols-2 gap-3 py-2">
        {(panelOptions || []).map((panel) => (
          <button
            key={panel.id}
            onClick={() => handlePanelSelect(panel)}
            className="group p-3 border border-[#E2E8F0] rounded-sm hover:border-[#0033A0] hover:bg-[#F4F6F8] transition-all text-center"
            data-testid={`select-panel-${panel.id}`}
          >
            <div className="w-14 h-20 mx-auto mb-2 p-1 bg-[#F4F6F8] rounded-sm group-hover:bg-white">
              <ProductIcon type={panel.icon} className="w-full h-full" />
            </div>
            <h3 className="text-sm font-medium text-[#0A0F1A] group-hover:text-[#0033A0]">{panel.name}</h3>
          </button>
        ))}
      </div>
    </div>
  );

  // Render atypical shapes
  const renderAtypicalStep = () => {
    if (!selectedTypeOption?.atypicalOptions) return null;
    
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 py-2">
        {(selectedTypeOption.atypicalOptions || []).map((shape) => (
          <button
            key={shape.id}
            onClick={() => handleAtypicalSelect(shape)}
            className="group p-3 border border-[#E2E8F0] rounded-sm hover:border-[#0033A0] hover:bg-[#F4F6F8] transition-all text-center"
            data-testid={`select-atypical-${shape.id}`}
          >
            <div className="w-14 h-14 mx-auto mb-2 p-1 bg-[#F4F6F8] rounded-sm group-hover:bg-white">
              <ProductIcon type={shape.icon} className="w-full h-full" />
            </div>
            <h3 className="text-xs font-medium text-[#0A0F1A] group-hover:text-[#0033A0]">
              {shape.name}
            </h3>
          </button>
        ))}
      </div>
    );
  };

  // Render simple subcategory selection (for non-multi-step categories)
  const renderSimpleSubcategories = () => {
    if (!selectedCategory) return null;
    
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {(selectedCategory.subcategories || []).map((sub) => (
          <button
            key={sub.id}
            onClick={() => handleSimpleSubcategorySelect(sub.id)}
            className="group p-3 border border-[#E2E8F0] rounded-sm hover:border-[#0033A0] hover:bg-[#F4F6F8] transition-all text-left"
            data-testid={`select-subcategory-${sub.id}`}
          >
            <div className="w-10 h-10 mb-2 p-1 bg-[#F4F6F8] rounded-sm group-hover:bg-white">
              <ProductIcon type={sub.icon} className="w-full h-full" />
            </div>
            <h3 className="text-sm font-medium text-[#0A0F1A] group-hover:text-[#0033A0]">
              {sub.name}
            </h3>
            <p className="text-xs text-[#4A5568] mt-1">
              da €{(priceList[sub.id]?.pvc) || sub.defaultPrice}/m²
            </p>
          </button>
        ))}
      </div>
    );
  };

  // Main render
  const renderContent = () => {
    if (!selectedCategory) return renderCategoryGrid();
    
    if (flowStep === 'simple') return renderSimpleSubcategories();
    if (flowStep === 'type') return renderTypeStep();
    if (flowStep === 'transom') return renderTransomStep();
    if (flowStep === 'opening') return renderOpeningStep();
    if (flowStep === 'panel') return renderPanelStep();
    if (flowStep === 'atypical') return renderAtypicalStep();
    
    return renderCategoryGrid();
  };

  const subtitle = selectedCategory ? getSubtitle() : '';

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-outfit text-xl" data-testid="modal-title">
            {getTitle()}
          </DialogTitle>
          {subtitle && (
            <p className="text-sm text-[#4A5568] mt-0.5">{subtitle}</p>
          )}
        </DialogHeader>

        {/* Step indicator for multi-step flows */}
        {selectedCategory && flowStep !== 'simple' && flowStep !== 'atypical' && getStepIndicator()}

        {/* Back button */}
        {selectedCategory && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBack}
            className="mb-2 text-[#4A5568] -mt-2"
            data-testid="back-button"
          >
            <ArrowLeft size={16} className="mr-1" />
            {flowStep === 'type' || flowStep === 'simple' ? 'Torna alle categorie' : 'Indietro'}
          </Button>
        )}

        {renderContent()}
      </DialogContent>
    </Dialog>
  );
}
